const DELIMITER = ','

const CHART_TYPE_CN: Record<string, string> = {
  bar: '柱状图',
  column: '条形图',
  line: '折线图',
  area: '面积图',
  scatter: '散点图',
  pie: '饼图',
  ring: '环形图',
  radar: '雷达图',
}

const getValue = (source: any, path: Array<string | number>, defaultValue?: any) => {
  let current = source
  for (const key of path) {
    if (current == null) return defaultValue
    current = current[key]
  }
  return current == null ? defaultValue : current
}

const extractTextFromHtml = (html: any) => {
  if (typeof html !== 'string') return ''
  const entities: Record<string, string> = {
    '&nbsp;': ' ',
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
    '&apos;': "'",
  }
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&(nbsp|amp|lt|gt|quot|#39|apos);/g, (match) => entities[match] || match)
    .replace(/\s+/g, ' ')
    .trim()
}

const csvCell = (value: any) => {
  const text = value == null ? '' : String(value)
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

const chartTypeName = (type: string) => CHART_TYPE_CN[type] || type || '图表'

const chartDataToCsv = (chartType: string, chartData: any) => {
  const labels = getValue(chartData, ['labels'], [])
  const series = getValue(chartData, ['series'], [])
  if (!Array.isArray(labels) || !Array.isArray(series) || labels.length === 0) return ''

  if (chartType === 'pie' || chartType === 'ring') {
    const rows = [['名称', '数值'].join(DELIMITER)]
    labels.forEach((label, index) => {
      rows.push([
        csvCell(label == null || label === '' ? `扇区${index + 1}` : String(label)),
        csvCell(getValue(series, [0, index], 0)),
      ].join(DELIMITER))
    })
    return rows.join('\n')
  }

  if (chartType === 'scatter') {
    const xData = getValue(series, [0], [])
    const rows = [['x', 'y'].join(DELIMITER)]
    xData.forEach((x: any, index: number) => {
      rows.push([csvCell(x), csvCell(getValue(series, [1, index], 0))].join(DELIMITER))
    })
    return rows.join('\n')
  }

  const legends = getValue(chartData, ['legends'], [])
  const legendList = Array.isArray(legends) && legends.length > 0
    ? legends
    : series.map((_item: any, index: number) => `系列${index + 1}`)
  const rows = [[''].concat(legendList.map(csvCell)).join(DELIMITER)]
  labels.forEach((label, rowIndex) => {
    const row = [label == null || label === '' ? `行${rowIndex + 1}` : String(label)]
    legendList.forEach((_legend: any, seriesIndex: number) => {
      row.push(csvCell(getValue(series, [seriesIndex, rowIndex], 0)))
    })
    rows.push(row.join(DELIMITER))
  })
  return rows.join('\n')
}

export const buildEnhancePptParams = (pptJson: any) => {
  try {
    const slides = getValue(pptJson, ['slides'], [])
    if (!Array.isArray(slides)) {
      return {
        success: false,
        error: '参数 slides 不是数组，请检查输入数据是否为合法的PPT JSON结构',
        pages: [],
      }
    }

    const pages = slides.map((slide, index) => {
      const elements = getValue(slide, ['elements'], [])
      const textLines: string[] = []
      const charts: Array<{ chart_type: string; chart_name: string; csv: string }> = []

      ;(Array.isArray(elements) ? elements : []).forEach((el) => {
        const type = getValue(el, ['type'], '')
        let rawContent = ''
        if (type === 'text') {
          rawContent = getValue(el, ['content'], '')
        } else if (type === 'shape' && el?.text && typeof el.text === 'object') {
          rawContent = getValue(el, ['text', 'content'], '')
        }

        if (rawContent) {
          const text = extractTextFromHtml(rawContent)
          if (text) textLines.push(text)
        }

        if (type === 'chart') {
          const chartType = getValue(el, ['chartType'], '')
          const csv = chartDataToCsv(chartType, getValue(el, ['data'], {}))
          if (csv) {
            charts.push({
              chart_type: chartType,
              chart_name: chartTypeName(chartType),
              csv,
            })
          }
        }
      })

      let content = textLines.join('\n')
      charts.forEach((chart, chartIndex) => {
        const chartSection = `[图表${chartIndex + 1}（${chart.chart_name}）数据]\n${chart.csv}`
        content = content ? `${content}\n${chartSection}` : chartSection
      })

      return {
        page: index + 1,
        content,
        charts,
      }
    })

    return {
      success: true,
      total_pages: pages.length,
      pages,
      formatted: pages.map((page) => `【第${page.page}页】\n${page.content}`).join('\n\n'),
    }
  } catch (error: any) {
    return {
      success: false,
      error: `处理失败：${error?.message || error}`,
      pages: [],
    }
  }
}
